/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import style from "../../../styles/common/formModal.module.css";
import EditDialog from "../../../Components/Common/EditDialog";
import FileField from "../../../Components/Masters/FileField";
import formatTitleWithOptional from "../../Scope1/OptionalFeild";

const Form = ({ isOpen, onClose, onSubmit, header, initialData, isEditing }) => {
  const toDateInputValue = (value) => {
    if (!value) return "";

    // Already in YYYY-MM-DD (or starts with it, e.g. ISO string)
    if (typeof value === "string") {
      const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

      // dd/mm/yyyy or dd-mm-yyyy
      const dmyMatch = value.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
      if (dmyMatch) {
        const dd = String(dmyMatch[1]).padStart(2, "0");
        const mm = String(dmyMatch[2]).padStart(2, "0");
        const yyyy = dmyMatch[3];
        return `${yyyy}-${mm}-${dd}`;
      }
    }

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().split("T")[0];
  };

  const initialFormData = {
    date: toDateInputValue(initialData?.date),
    capitalGoodsCategory: initialData?.capitalGoodsCategory || "",
    capitalGoodName: initialData?.capitalGoodName || "",
    quantityPurchased: initialData?.quantityPurchased || "",
    usefulLifeYears: initialData?.usefulLifeYears || "",
    fuelConsumptionForEnergyGeneration:
      initialData?.fuelConsumptionForEnergyGeneration || "",
    upstreamEmissionsFromElectricityGeneration:
      initialData?.upstreamEmissionsFromElectricityGeneration || "",
    fuelConsumptionForTransportation:
      initialData?.fuelConsumptionForTransportation || "",
    tAndDLossesFromElectricityPurchased:
      initialData?.tAndDLossesFromElectricityPurchased || "",
    generationOfPurchasedElectricitySoldToEndUsers:
      initialData?.generationOfPurchasedElectricitySoldToEndUsers || "",
    energyUsedForUpstreamProcesses:
      initialData?.energyUsedForUpstreamProcesses || "",
    documents: initialData?.documents || [],
  };

  const [editDialog, setEditDialog] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [formDataSnapshot, setFormDataSnapshot] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});

  useEffect(() => {
    setFormData(initialFormData);
    setFormDataSnapshot(initialFormData);
    setUploadedFiles(initialData?.documents || []);
  }, [isOpen, initialData]);

  const fieldConfig = [
    {
      label: "Date",
      name: "date",
      type: "date",
      required: true,
    },
    {
      label: "Capital Goods Category",
      name: "capitalGoodsCategory",
      type: "select",
      required: true,
      options: [
        "Production Materials",
        "Finished Goods",
        "Transportation & Distribution",
        "IT & Communication",
        "Professional Services",
        "Other",
      ],
    },
    {
      label: "Name of the Capital Good",
      name: "capitalGoodName",
      type: "text",
      required: true,
      placeholder: "Enter name",
    },
    {
      label: "Quantity Purchased",
      name: "quantityPurchased",
      type: "number",
      required: true,
      placeholder: "Enter quantity",
    },
    {
      label: "Useful Life (Years)",
      name: "usefulLifeYears",
      type: "number",
      required: true,
      placeholder: "Enter years",
    },
    {
      label: "Fuel consumption for energy generation",
      name: "fuelConsumptionForEnergyGeneration",
      type: "number",
      required: true,
      placeholder: "Enter value",
    },
    {
      label: "Upstream emissions from electricity generation",
      name: "upstreamEmissionsFromElectricityGeneration",
      type: "number",
      required: true,
      placeholder: "Enter value",
    },
    {
      label: "Fuel consumption for transportation",
      name: "fuelConsumptionForTransportation",
      type: "number",
      required: true,
      placeholder: "Enter value",
    },
    {
      label: "T&D losses from electricity purchased",
      name: "tAndDLossesFromElectricityPurchased",
      type: "number",
      required: true,
      placeholder: "Enter value",
    },
    {
      label: "Generation of Purchased Electricity Sold to End Users",
      name: "generationOfPurchasedElectricitySoldToEndUsers",
      type: "number",
      required: true,
      placeholder: "Enter value",
    },
    {
      label: "Energy used for upstream processes",
      name: "energyUsedForUpstreamProcesses",
      type: "number",
      required: true,
      placeholder: "Enter value",
    },
    {
      name: "document",
      type: "docs",
      placeholder: "Enter Document",
    },
  ];

  const handleExitAgree = () => {
    setEditDialog(false);
    setErrors({});
    setFormData(initialFormData);
    setFormDataSnapshot(initialFormData);
    setUploadedFiles([]);
    onClose();
  };

  const isFormChanged = () =>
    JSON.stringify(formData) !== JSON.stringify(formDataSnapshot);

  const handleClose = () => {
    if (!isFormChanged()) {
      handleExitAgree();
    } else {
      setEditDialog(true);
    }
  };

  const validateFormData = () => {
    const newErrors = {};

    fieldConfig.forEach((field) => {
      const value = formData[field.name];

      if (field.required) {
        if (value === "" || value === null || value === undefined) {
          newErrors[field.name] = `${field.label} is required.`;
          return;
        }
      }

      if (field.type === "number" && value !== "" && isNaN(Number(value))) {
        newErrors[field.name] = `${field.label} must be a number.`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (name, type) => (event) => {
    const { value } = event.target;

    if (type === "number" && value !== "" && !/^\d*\.?\d*$/.test(value)) return;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSubmit = () => {
    if (!validateFormData()) return;
    
    let formDataToSubmit = {
      ...formData,
      documents: uploadedFiles,
    };
    
    onSubmit(formDataToSubmit, isEditing);
    setUploadedFiles([]);
  };

  return (
    <Drawer
      sx={{
        backgroundColor: "transparent",
        "& .MuiDrawer-paper": {
          backgroundColor: "transparent",
          boxShadow: "none",
          height: "100vh",
          minHeight: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      anchor="right"
      open={isOpen}
      onClose={handleClose}
    >
      <Box
        sx={{
          width: "26rem",
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
        }}
      >
        {editDialog && (
          <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />
        )}
        <Box
          sx={{
            backgroundColor: "#fff",
            height: "20px",
            padding: "13px",
            margin: "30px 0 0 0",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "80%",
            width: "5%",
            zIndex: "1",
          }}
        >
          <IconButton onClick={handleClose} sx={{ color: "black" }}>
            <CloseIcon sx={{ fontSize: "1.2rem" }} />
          </IconButton>
        </Box>
        <Box className={style.main_div}>
          <Typography
            id="modal-title"
            variant="h6"
            align="left"
            sx={{ textDecoration: "underline", fontWeight: "bold", mb: 2 }}
          >
            {header}
          </Typography>

          <form>
            {fieldConfig.map((field) => (
              <div key={field.name} style={{ margin: "1rem 0" }}>
                {field.type === "docs" ? (
                  <FileField
                    title={formatTitleWithOptional("Upload Documents (optional)")}
                    name={field.name}
                    errors={errors[field.name]}
                    fileUrls={uploadedFiles}
                    progress={uploadProgress[field.name] || []}
                    {...{
                      setUploadedFiles,
                      setUploadProgress,
                      setErrors,
                    }}
                  />
                ) : (
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mt: 1 }}
                    >
                      {field.label}
                    </Typography>

                    {field.type === "select" ? (
                  <TextField
                    select
                    size="small"
                    fullWidth
                    required={field.required}
                    value={formData[field.name] || ""}
                    onChange={handleChange(field.name, field.type)}
                    error={Boolean(errors[field.name])}
                    helperText={errors[field.name] || ""}
                    SelectProps={{
                      displayEmpty: true,
                    }}
                  >
                    <MenuItem value="" disabled>
                      Tap to select
                    </MenuItem>
                    {field.options.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    type={field.type === "date" ? "date" : "text"}
                    size="small"
                    fullWidth
                    required={field.required}
                    placeholder={field.placeholder || ""}
                    value={formData[field.name] || ""}
                    onChange={handleChange(field.name, field.type)}
                    error={Boolean(errors[field.name])}
                    helperText={errors[field.name] || ""}
                    inputProps={
                      field.type === "date"
                        ? { max: new Date().toISOString().split("T")[0] }
                        : {}
                    }
                  />
                    )}
                  </>
                )}
              </div>
            ))}
          </form>

          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ backgroundColor: "#1C486B", textTransform: "none" }}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Form;

